/**
 * Google Consent Mode v2 helpers.
 *
 * GTM loads unconditionally in prod/staging. The four storage categories
 * default to `denied` for EEA + GB users; tags inside the container respect
 * those signals until the user accepts in the banner, at which point we push
 * an `update` to `granted`. An explicit `denied` update on decline flushes
 * Google's `wait_for_update` timer so cookieless pings flow immediately.
 */

import { CONSENT_COOKIE_NAME, CONSENT_VERSION } from "./consent.shared";

type ConsentState = "granted" | "denied";

type ConsentCategoryMap = {
  ad_storage: ConsentState;
  analytics_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
};

function categoryMap(state: ConsentState): ConsentCategoryMap {
  return {
    ad_storage: state,
    analytics_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  };
}

const DEFAULT_CONFIG = {
  ...categoryMap("denied"),
  wait_for_update: 500,
  region: ["EEA", "GB"],
};

/**
 * Inline bootstrap script. Initializes `dataLayer` + a `gtag` shim and pushes
 * the default consent state. Must run before the GTM snippet — render it as
 * a raw `<script>` immediately before `<GoogleTagManager>` so it executes
 * during HTML parsing.
 *
 * When the user already has a consent decision, an `update` is pushed alongside
 * the default so tags see the resolved state on first fire instead of waiting on
 * the 500ms timer. The decision is read from the cookie **in the browser**, not
 * baked in per request: the script is identical for every visitor, so the HTML
 * carrying it can be cached. Baking it in meant a cached page bootstrapped every
 * returning visitor as denied.
 */
export function buildConsentBootstrapScript(): string {
  return [
    "window.dataLayer=window.dataLayer||[];",
    "window.gtag=window.gtag||function(){window.dataLayer.push(arguments);};",
    `window.gtag('consent','default',${JSON.stringify(DEFAULT_CONFIG)});`,
    "window.gtag('set','url_passthrough',true);",
    "window.gtag('set','ads_data_redaction',true);",
    "try{",
    `var m=document.cookie.match(/(?:^|; *)${CONSENT_COOKIE_NAME}=([^;]*)/);`,
    "var p=m?decodeURIComponent(m[1]).split(':'):[];",
    `if(Number(p[1])===${CONSENT_VERSION}){`,
    `if(p[0]==='accepted')window.gtag('consent','update',${JSON.stringify(categoryMap("granted"))});`,
    `else if(p[0]==='declined')window.gtag('consent','update',${JSON.stringify(categoryMap("denied"))});`,
    "}",
    "}catch(e){}",
  ].join("");
}

/**
 * Push a Consent Mode update from the browser. No-ops when `window.gtag`
 * isn't defined — that means the bootstrap script never ran (e.g., GTM is
 * not configured in this environment), so there's nothing to update.
 */
export function pushConsentUpdate(state: ConsentState): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  w.gtag?.("consent", "update", categoryMap(state));
}
